import { cn } from "@/lib/utils";

/**
 * Circular progress ring. Unifies the order-board ProgressRing and the cooking
 * ProgressCircle. Track is the neutral gray ramp; the arc tone is explicit so
 * callers map their own state (brand-blue, ink, late, warning).
 */
type RingSize = "sm" | "md" | "lg";
type RingTone = "brand" | "ink" | "late" | "warning";

const SIZE: Record<RingSize, { box: string; viewBox: number; r: number; sw: number; label: string }> = {
  sm: { box: "size-11.5", viewBox: 40, r: 17, sw: 3.5, label: "text-[0.6875rem]" },
  md: { box: "size-13", viewBox: 52, r: 22, sw: 5, label: "text-[0.6875rem]" },
  lg: { box: "size-17", viewBox: 40, r: 17, sw: 3.5, label: "text-sm" },
};

const TONE: Record<RingTone, string> = {
  brand: "stroke-(--brand-blue)",
  ink: "stroke-(--brand-black)",
  late: "stroke-destructive",
  warning: "stroke-(--color-warning)",
};

export interface ProgressRingProps {
  /** 0–100. */
  pct: number;
  size?: RingSize;
  tone?: RingTone;
  showLabel?: boolean;
  className?: string;
  "aria-label"?: string;
}

export function ProgressRing({
  pct,
  size = "md",
  tone = "brand",
  showLabel = true,
  className,
  "aria-label": ariaLabel,
}: ProgressRingProps) {
  const s = SIZE[size];
  const center = s.viewBox / 2;
  const circ = 2 * Math.PI * s.r;
  const clamped = Math.min(Math.max(pct, 0), 100);
  const offset = circ * (1 - clamped / 100);

  return (
    <div
      className={cn("relative shrink-0", s.box, className)}
      aria-label={ariaLabel ?? `${Math.round(clamped)}%`}
    >
      <svg
        className="size-full -rotate-90"
        viewBox={`0 0 ${s.viewBox} ${s.viewBox}`}
      >
        <circle
          cx={center}
          cy={center}
          r={s.r}
          fill="none"
          strokeWidth={s.sw}
          className="stroke-(--gray-200)"
        />
        <circle
          cx={center}
          cy={center}
          r={s.r}
          fill="none"
          strokeWidth={s.sw}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          className={TONE[tone]}
          style={{ transition: "stroke-dashoffset 0.4s ease" }}
        />
      </svg>
      {showLabel ? (
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center font-semibold text-(--color-text-primary)",
            s.label,
          )}
        >
          {Math.round(clamped)}%
        </span>
      ) : null}
    </div>
  );
}
